import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Company {
  id: number;
  /** The signed-in user's role in this company (only on the /me/companies list). */
  role?: 'company_admin' | 'company_associate' | 'read_only';
  name: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  telephone: string | null;
  paidThrough: string | null;
  a01: boolean | null;
  a02: boolean | null;
  a03: boolean | null;
  a04: boolean | null;
  a05: boolean | null;
  a06: boolean | null;
  a07: boolean | null;
  a08: boolean | null;
  a09: boolean | null;
  a10: boolean | null;
  a11: boolean | null;
  a12: boolean | null;
  a13: boolean | null;
  unsureQuestions: string[];
}

export interface SubscriptionView {
  tier: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  status: 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED';
  currentPeriodEnd: string | null;
  trialEndsAt: string | null;
  cancelledAt: string | null;
  isUsable: boolean;
  isReadOnly: boolean;
}

export function useMyCompanies() {
  return useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get<Company[]>('/api/Clients/me/companies'),
    staleTime: 60_000,
  });
}

export function useCompany(id: number | undefined) {
  return useQuery({
    queryKey: ['company', id],
    queryFn: () => api.get<Company>('/api/Companies'),
    enabled: !!id,
  });
}

export type UpdateCompanyInput = Partial<Omit<Company, 'id' | 'paidThrough'>>;

export function useUpdateCompany(id: number | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateCompanyInput) =>
      api.patch<Company>('/api/Companies', data),
    onSuccess: () => {
      // Profile-question answers change which assessment items are visible, so
      // refresh the company record, the company list (dashboard banner), and
      // every assessment/report summary that filters on the profile.
      qc.invalidateQueries({ queryKey: ['company', id] });
      qc.invalidateQueries({ queryKey: ['companies'] });
      qc.invalidateQueries({ queryKey: ['module-summary'] });
      qc.invalidateQueries({ queryKey: ['response-summary'] });
    },
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string }) =>
      api.post<Company>('/api/Clients/me/companies', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

export function useSubscription(companyId: number | undefined) {
  return useQuery({
    queryKey: ['subscription', companyId],
    queryFn: () => api.get<SubscriptionView | null>('/api/Companies/subscription'),
    enabled: !!companyId,
  });
}

export function useActivity(companyId: number | undefined) {
  return useQuery({
    queryKey: ['activity', companyId],
    queryFn: () =>
      api.get<
        Array<{
          id: string;
          action: string;
          entityType: string;
          entityId: string;
          createdAt: string;
          user: { id: number; name: string | null; email: string } | null;
        }>
      >('/api/Companies/activity'),
    enabled: !!companyId,
  });
}
